import { useState, useMemo } from 'react';
import { SEO } from '../../utils/seo';
import { faqItems, faqCategories } from '../../mocks/faqData';
import HeroSection from './components/HeroSection';
import CategoryNav from './components/CategoryNav';
import PopularQuestions from './components/PopularQuestions';
import SearchBar from './components/SearchBar';
import FaqList from './components/FaqList';
import CtaSection from './components/CtaSection';

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.slice(0, 10).map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const categoryMap = useMemo(() => {
    const map: Record<string, { label: string; icon: string }> = {};
    faqCategories.forEach((cat) => {
      map[cat.id] = { label: cat.label, icon: cat.icon };
    });
    return map;
  }, []);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: faqItems.length };
    faqItems.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, []);

  const filteredItems = useMemo(() => {
    let items = faqItems;

    if (activeCategory !== 'all') {
      items = items.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q)
      );
    }

    return items;
  }, [activeCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="FAQ - Frequently Asked Questions | Greenfield Partners"
        description="Answers to common questions about precision aerial application, drone spraying, pricing, services, and working with Greenfield Partners."
        keywords="agricultural drone FAQ, aerial application questions, drone spraying cost, precision agriculture answers"
        schema={faqSchema}
      />
      <HeroSection />

      <CategoryNav
        activeCategory={activeCategory}
        onCategoryChange={(id) => {
          setActiveCategory(id);
          setSearchQuery('');
        }}
        categoryCounts={categoryCounts}
      />

      <PopularQuestions />

      <SearchBar value={searchQuery} onChange={setSearchQuery} />

      <div className="max-w-4xl mx-auto px-6 pb-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            Showing <strong className="text-gray-800">{filteredItems.length}</strong>{' '}
            {filteredItems.length === 1 ? 'question' : 'questions'}
            {activeCategory !== 'all' && (
              <span>
                {' '}in <strong className="text-emerald-700">{categoryMap[activeCategory]?.label}</strong>
              </span>
            )}
            {searchQuery && (
              <span>
                {' '}matching &quot;<strong className="text-gray-800">{searchQuery}</strong>&quot;
              </span>
            )}
          </p>
          {(activeCategory !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                setActiveCategory('all');
                setSearchQuery('');
              }}
              className="text-sm text-emerald-700 hover:text-emerald-800 font-medium cursor-pointer whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>

        <FaqList items={filteredItems} categoryMap={categoryMap} />
        
        {/* Helpful Links Section */}
        <div className="mt-12 bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-links-line text-emerald-700"></i>
            Helpful Resources
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href="/instant-quote" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-calculator-line text-emerald-600"></i>
              Get an Instant Quote
            </a>
            <a href="/roi-calculator" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-line-chart-line text-emerald-600"></i>
              Calculate Your ROI
            </a>
            <a href="/seasonal-programs" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-calendar-check-line text-emerald-600"></i>
              View Seasonal Programs
            </a>
            <a href="/operator-checklist" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-checkbox-multiple-line text-emerald-600"></i>
              Operator Checklist Tool
            </a>
            <a href="/services" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-service-line text-emerald-600"></i>
              All Services
            </a>
            <a href="/how-we-work" className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm">
              <i className="ri-route-line text-emerald-600"></i>
              How We Work
            </a>
          </div>
        </div>
      </div>

      <CtaSection />
    </div>
  );
}
